/**
 * API client for the products page's "Check categories" flow.
 *
 * The check is sent ONE CHUNK AT A TIME (`CATEGORY_CHECK_CHUNK_SIZE` rows per request) so the modal
 * can draw real progress and the user can cancel mid-run. A 2000-product catalog is minutes of
 * model time; one request that long has no progress and dies on a proxy timeout.
 *
 * Server side: `server/domains/categoryCheck/categoryCheckService.ts`.
 */

import { getAccessToken } from '../authentication/supabaseService';

/**
 * Rows per /categoryCheck/check request. Must stay at or below the route's own
 * CATEGORY_CHECK_MAX_ROWS_PER_REQUEST (200), which rejects anything larger.
 */
export const CATEGORY_CHECK_CHUNK_SIZE = 100;

export interface CategoryCheckRow {
  /** Opaque row identity, echoed back on the finding. The page keys its rows by it. */
  rowKey: string;
  restaurantPlatformId: string;
  platform: string;
  articleId?: string;
  productName: string;
  mainCategory?: string;
  category?: string;
  /**
   * The real supplier behind an invoice-only row (`unknownSupplierName`). On `any_invoice` the
   * platform is a bucket rather than a vendor, so without this the reviewer sees every uploaded
   * invoice as one supplier — and supplier context is what tells a cleaning product from a drink.
   */
  supplierName?: string;
}

export type CategoryCheckVerdict = 'wrong' | 'missing';

export interface CategoryCheckFinding {
  rowKey: string;
  verdict: CategoryCheckVerdict;
  productName: string;
  platform: string;
  currentMainCategory?: string;
  currentCategory?: string;
  suggestedMainCategory: string;
  suggestedCategory: string;
  confidence: number;
  reason: string;
}

export interface CategoryCheckChunkResult {
  findings: CategoryCheckFinding[];
  checked: number;
  skipped: number;
  aiCalls: number;
  failedBatches: number;
}

export interface CategoryFix {
  rowKey: string;
  restaurantPlatformId: string;
  articleId?: string;
  productName: string;
  mainCategory: string;
  category: string;
  /** The row's current subcategories; the server drops the ones illegal under the new category. */
  subCategories?: string[];
}

export interface CategoryFixOutcome {
  rowKey: string;
  productName: string;
  updateCount?: number;
  error?: string;
}

export interface CategoryApplySummary {
  applied: number;
  failed: number;
  ordersUpdated: number;
  outcomes: CategoryFixOutcome[];
}

/**
 * Audit one chunk. Writes nothing.
 *
 * `restaurantId` is used ONLY to attribute the AI spend on the admin AI Costs page — the check
 * itself works purely off `rows`. Pass null in the All Restaurants view: its selection is a
 * sentinel id, not a restaurant, and booking cost against it would invent a row on that page.
 *
 * `signal` carries the modal's Cancel — an aborted fetch rejects, which the caller treats as a stop
 * rather than an error.
 */
export async function checkCategoryChunk(
  restaurantId: string | null,
  rows: CategoryCheckRow[],
  signal?: AbortSignal
): Promise<CategoryCheckChunkResult> {
  const token = await getAccessToken();
  const res = await fetch('/api/orderhistory/categoryCheck/check', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ restaurantId, rows }),
    signal,
  });

  if (!res.ok) {
    throw new Error(`Failed to check categories: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

/** Apply the accepted fixes. Each one lands account-wide across the whole order history. */
export async function applyCategoryFixes(fixes: CategoryFix[]): Promise<CategoryApplySummary> {
  const token = await getAccessToken();
  const res = await fetch('/api/orderhistory/categoryCheck/apply', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ fixes }),
  });

  if (!res.ok) {
    throw new Error(`Failed to apply category fixes: ${res.status} ${res.statusText}`);
  }

  return res.json();
}
